import { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  message,
  Space,
  Divider,
  Alert,
  Form,
  Typography,
} from "antd";
import {
  UserAddOutlined,
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import AdminCreatedSuccessModal from "./AdminCreatedSuccessModal";

const { Title } = Typography;

const SuperAdminAddAdminModal = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [createNewCompany, setCreateNewCompany] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [createdAdminData, setCreatedAdminData] = useState(null);

  // ================= INIT =================
  useEffect(() => {
    if (visible) {
      fetchCompanies();
      form.resetFields();
      setCreateNewCompany(false);
    }
  }, [visible]);

  // ================= FETCH COMPANIES =================
  const fetchCompanies = async () => {
    try {
      const response = await apiClient.get("/superadmin/companies");
      setCompanies(response?.data || []);
    } catch (error) {
      console.error(error);
      setCompanies([]);
    }
  };

  // ================= SUBMIT =================
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.toLowerCase().trim(),
        phone: values.phone,
        role: "admin",
        ...(createNewCompany
          ? {
              companyName: values.companyName,
              companyAddress: values.companyAddress,
            }
          : {
              companyId: values.companyId,
            }),
      };

      const response = await apiClient.post(
        "/superadmin/admins",
        payload
      );

      if (!response?.success) {
        throw new Error(response?.message || "Creation failed");
      }

      const adminData = response.data;

      setCreatedAdminData(adminData);
      form.resetFields();
      setCreateNewCompany(false);
      setSuccessModalVisible(true);

      onSuccess?.();
    } catch (error) {
      console.error(error);

      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create admin"
      );
    } finally {
      setLoading(false);
    }
  };

  // ================= CLOSE =================
  const handleCancel = () => {
    form.resetFields();
    setCreateNewCompany(false);
    onClose?.();
  };

  const handleSuccessModalClose = () => {
    setSuccessModalVisible(false);
    setCreatedAdminData(null);
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <UserAddOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Add New Admin
            </Title>
          </Space>
        }
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Alert
          message="Admin Onboarding"
          description="A secure setup link will be generated. Share it with the admin to complete account setup."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          {/* ADMIN DETAILS */}
          <Title level={5}>
            <UserAddOutlined /> Admin Details
          </Title>

          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Enter name" }, { min: 2 }]}
          >
            <Input size="large" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true }, { type: "email" }]}
          >
            <Input prefix={<MailOutlined />} size="large" />
          </Form.Item>

          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input prefix={<PhoneOutlined />} size="large" />
          </Form.Item>

          <Divider />

          {/* COMPANY */}
          <Title level={5}>
            <BankOutlined /> Company
          </Title>

          <Form.Item name="companyOption" rules={[{ required: true }]}>
            <Select
              size="large"
              placeholder="Select option"
              onChange={(v) => setCreateNewCompany(v === "new")}
            >
              <Select.Option value="existing">Existing Company</Select.Option>
              <Select.Option value="new">New Company</Select.Option>
            </Select>
          </Form.Item>

          {!createNewCompany ? (
            <Form.Item
              name="companyId"
              rules={[{ required: true, message: "Select company" }]}
            >
              <Select size="large" showSearch placeholder="Select company">
                {companies.map((c) => (
                  <Select.Option key={c._id} value={c._id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="companyName"
                rules={[{ required: true, message: "Enter company name" }]}
              >
                <Input placeholder="Company Name" size="large" />
              </Form.Item>

              <Form.Item
                name="companyAddress"
                rules={[{ required: true, message: "Enter address" }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}

          <Divider />

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCancel}>Cancel</Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<UserAddOutlined />}
              >
                {createNewCompany
                  ? "Create Admin & Company"
                  : "Create Admin"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <AdminCreatedSuccessModal
        visible={successModalVisible}
        onClose={handleSuccessModalClose}
        adminData={createdAdminData}
      />
    </>
  );
};

export default SuperAdminAddAdminModal;
